<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { ChevronRight, CloudUpload, FileInput, FileJson, History } from 'lucide-vue-next'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { Button, Card, message, Modal } from 'ant-design-vue'
  import UploadNewDataset from './components/AdvancedAnalysisUploadNewDataset.vue'
  import { WizardStepContent } from '@/shared/Wizard/models/WizardStepContent'
  import { WizardStep, WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { AplosFile } from '@/components/FileManager/models/AplosFile'
  import { useAppLayoutMode } from '@/composables/useAppLayoutMode'
  import { AppLayoutMode } from '@/models/AppLayoutMode'
  import FileManager from '@/components/FileManager/components/FileManager.vue'
  import ExecutionHistory from '@/components/ExecutionHistory/ExecutionHistory.vue'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { useRouter } from 'vue-router'
  import { AdvancedAnalysisWizardSharedModel } from '../models/AdvancedAnaylsisWizardSharedModel'
  import { AdvancedAnalysisWizardModel } from '../models/AdvancedAnalysisWizardModel'
  import { AdvancedAnalysisWizardNavigationService } from '../services/AdvancedAnalysisWizardNavigationService'
  import { AdvancedAnalysisWizardStepType } from '../models/AdvancedAnyalsisWizardStepType'
  import { AdvancedAnalysisPhaseType } from '@/shared/Executions/models/ExecutionPhaseType'

  export interface IInputDataStepProps {
    step: WizardStep<AdvancedAnalysisWizardSharedModel>
    stepContent: WizardStepContent<AdvancedAnalysisWizardSharedModel>
    wizardModel: AdvancedAnalysisWizardModel
  }

  const props = defineProps<IInputDataStepProps>()

  const router = useRouter()

  const uploadNewDatasetRef = ref<any>(null)
  const isUploading = ref<boolean>(false)
  const uploadNewDatasetHasFileSelection = ref<boolean>(false)

  const emit = defineEmits<{
    (e: 'update:inputFile', value: AplosFile): void
  }>()

  const wizardNavigationService: AdvancedAnalysisWizardNavigationService =
    InjectionService.injectStrict(AdvancedAnalysisWizardNavigationService.injectableKey)

  // Single method for handling how data is set
  const initializeInputDataset = async (file: AplosFile) => {
    try {
      router.push({
        path: '/app/advanced-analysis',
        query: {
          step: AdvancedAnalysisWizardStepType.InputData,
          analysisFileId: file?.id
        }
      })
    } catch (e: any) {
      message.error(
        `Error encountereed attempting to initialize dataset for file: ${file}`,
        defaultMessageDurationSeconds
      )
    } finally {
      props.step.isLoading = false
    }
  }

  const onUploadNewDatasetUploadClicked = async (event?: Event): Promise<void> => {
    const start = async () => {
      props.step.isLoading = true
      props.step.loadingMessage = 'Uploading new dataset...'
      isUploading.value = true
      try {
        await uploadNewDatasetRef.value?.startUpload(event)
      } finally {
        isUploading.value = false
        props.step.isLoading = false
      }
    }

    if (props.wizardModel.sharedModel!.inputFile != null) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Uploading a new dataset will reset your Advanced Analysis.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          void start()
        },
        onCancel() {}
      })
    } else {
      await start()
    }
  }

  // Handle file selection from FileManager
  const handleFileSelected = async (file: AplosFile) => {
    if (file.id) {
      onFileSelected(file)
    } else {
      message.error('Invalid file selected', defaultMessageDurationSeconds)
    }
  }

  const onFileSelected = async (file: AplosFile) => {
    if (props.wizardModel.sharedModel!.inputFile != null) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Selecting a new dataset or previous analysis will reset your Advanced Anaylsis.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          initializeInputDataset(file)
        },
        onCancel() {}
      })
    } else {
      initializeInputDataset(file)
    }
  }

  const onAdvancedAnalysisClicked = async (execution: Execution): Promise<void> => {
    if (
      props.wizardModel.sharedModel!.inputFile != null ||
      props.wizardModel?.sharedModel?.advancedAnalysis != null
    ) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Selecting a new dataset or previous analysis will reset your Advanced Anaylsis.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          handleAdvancedAnalysisClicked(execution)
        },
        onCancel() {}
      })
    } else {
      handleAdvancedAnalysisClicked(execution)
    }
  }

  const handleAdvancedAnalysisClicked = async (execution: Execution): Promise<void> => {
    if (
      execution == null ||
      execution.id == null ||
      execution.name == null ||
      execution.input?.fileId == null
    ) {
      message.error('Invalid previous execution data recieved. Cannot load analysis data!')
      throw Error('invalid execution argument value')
    }

    router.push({
      path: '/app/advanced-analysis',
      query: {
        step: AdvancedAnalysisWizardStepType.AdvancedAnalysisConfig,
        analysisFileId: execution?.input?.fileId!,
        advancedAnalysisExecutionId: execution.id
      }
    })
  }

  type OptionId = 'upload' | 'previousDataset' | 'loadAdvancedAnalysis'

  const options = [
    { id: 'upload' as OptionId, label: 'Upload New Dataset', icon: CloudUpload, disabled: false },
    {
      id: 'previousDataset' as OptionId,
      label: 'Analyze Previously Uploaded Dataset',
      icon: FileInput,
      disabled: false
    },
    {
      id: 'loadAdvancedAnalysis' as OptionId,
      label: 'Load Previous Analysis',
      icon: History,
      disabled: false
    }
  ]

  const selectedOption = ref<OptionId>('upload')

  const selectOption = (id: OptionId) => {
    selectedOption.value = id
  }

  const goToWizardStepByType = (stepType: AdvancedAnalysisWizardStepType) => {
    wizardNavigationService.setActiveWizardStepByType(stepType, props.wizardModel)
  }

  const goToAdvancedAnalysisConfigDisabled = computed(() => {
    return !props.wizardModel.sharedModel!.inputFile
  })

  const isUploadButtonDisabled = computed(
    () => isUploading.value === true || uploadNewDatasetHasFileSelection.value === false
  )

  const onUploadNewDatasetFilesSelected = (files?: File[]): void => {
    uploadNewDatasetHasFileSelection.value = !!files?.length
  }

  const onUploadNewDatasetUploadComplete = (): void => {
    // Allow UploadNewDataset to reset its internals and clear the file selection state
    uploadNewDatasetHasFileSelection.value = false
    uploadNewDatasetRef.value?.resetUpload?.()
  }

  onMounted(() => {
    if (props.wizardModel?.activeStep && !props.wizardModel?.sharedModel?.inputFile) {
      props.wizardModel.activeStep.statusType = WizardStepStatusType.Warnings
      props.wizardModel.activeStep.statusMessage = 'No dataset selected'
    }
  })

  const { isMediumTierOrLess } = useWindowSize()

  // derive layout mode so this step adapts to FitToBrowser vs FitToContent
  const { layoutMode } = useAppLayoutMode()
  const isFitToBrowser = computed(() => layoutMode.value === AppLayoutMode.FitToBrowser)
</script>

<template>
  <div
    :class="[
      'input-data-step-container',
      'min-h-0',
      'min-w-[1010px]',
      'flex-col',
      'items-start',
      'gap-4',
      'bg-background',
      { 'fit-to-browser input-data-step-fill flex': isFitToBrowser }
    ]"
  >
    <div
      class="flex w-full items-center border-b bg-[var(--card-lighter)] p-4 dark:bg-[var(--card)]"
    >
      <div class="flex-1">
        <h3 class="text-lg font-semibold">Input Data</h3>
        <p class="mt-100 text-sm">Select input data to begin configuring advanced analysis</p>
      </div>

      <div class="flex items-center gap-1">
        <!-- Go to Analysis Config -->
        <a-tooltip>
          <template #title>Go to Advanced Analysis</template>
          <Button
            v-if="isMediumTierOrLess"
            type="primary"
            shape="circle"
            size="large"
            class="flex items-center justify-center"
            :disabled="goToAdvancedAnalysisConfigDisabled"
            @click="
              () => {
                goToWizardStepByType(AdvancedAnalysisWizardStepType.AdvancedAnalysisConfig)
              }
            "
          >
            <FileJson class="h-4 w-4" />
          </Button>
          <Button
            v-else
            type="primary"
            :disabled="goToAdvancedAnalysisConfigDisabled"
            @click="
              () => {
                goToWizardStepByType(AdvancedAnalysisWizardStepType.AdvancedAnalysisConfig)
              }
            "
          >
            <span class="ant-button-flex-content">
              {{ 'Advanced Analysis' }}
              <ChevronRight class="h-4 w-4" />
            </span>
          </Button>
        </a-tooltip>
      </div>
    </div>

    <!-- Options row: small cards aligned horizontally, top-aligned -->
    <div class="mt-5 flex w-full justify-evenly gap-6 pl-5 pr-5">
      <template v-for="opt in options.filter((x) => x.disabled !== true)" :key="opt.id">
        <!-- For small widths render icon-only similar to dashboard -->
        <a-tooltip v-if="isMediumTierOrLess">
          <template #title>{{ opt.label }}</template>
          <Button
            type="default"
            shape="circle"
            size="large"
            class="dashboard-option-circle flex items-center justify-center"
            :class="{
              selected: selectedOption === opt.id
            }"
            @click="selectOption(opt.id)"
            title="View Data"
            tooltip="View Data"
          >
            <div class="flex items-center justify-center rounded-full p-2">
              <component
                :is="opt.icon"
                class="dashboard-option-icon text-base"
                :class="{
                  selected: selectedOption === opt.id
                }"
                aria-hidden="true"
              />
            </div>
          </Button>
        </a-tooltip>

        <!-- For larger widths render label + right-side icon to match dashboard layout -->
        <button
          v-else
          type="button"
          role="button"
          :aria-pressed="selectedOption === opt.id"
          @click="selectOption(opt.id)"
          class="dashboard-option-btn card-btn flex min-w-[12rem] items-center justify-between rounded-md px-3 py-2 text-sm"
          :class="{
            selected: selectedOption === opt.id
          }"
        >
          <div class="ml-2 flex h-full flex-col items-start justify-center text-left">
            <span :class="['font-medium']">{{ opt.label }}</span>
          </div>

          <div
            class="ml-4 mr-2 flex items-center justify-center rounded-full bg-blue-100 p-2 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
          >
            <component :is="opt.icon" class="dashboard-option-icon" aria-hidden="true" />
          </div>
        </button>
      </template>
    </div>

    <div class="w-full pb-8 pl-8 pr-8 pt-2">
      <div
        class="input-data-step-content mt-5 min-h-0 w-full"
        :class="{ 'flex-1 overflow-auto': isFitToBrowser }"
      >
        <!-- listen for the FileUpload emitted payload -->

        <div v-if="selectedOption === 'upload'" class="flex w-full gap-4">
          <UploadNewDataset
            ref="uploadNewDatasetRef"
            v-if="selectedOption === 'upload'"
            v-on:upload-clicked="onUploadNewDatasetUploadClicked"
            :shared-model="props.wizardModel.sharedModel"
            :hide-upload-button="true"
            @input-file-set="initializeInputDataset"
            @files-selected="onUploadNewDatasetFilesSelected"
            @upload-complete="onUploadNewDatasetUploadComplete"
          >
            <template #uploadButton>
              <Button
                type="primary"
                size="large"
                class="mt-4"
                :disabled="isUploadButtonDisabled"
                @click="onUploadNewDatasetUploadClicked"
              >
                Upload
              </Button>
            </template>
          </UploadNewDataset>
        </div>

        <FileManager
          class="dark:bg-[#0d1526]"
          v-if="selectedOption === 'previousDataset'"
          @file-selected="handleFileSelected"
          :selectable="true"
          :show-actions="false"
          :show-header="false"
        />

        <Card
          v-if="selectedOption === 'loadAdvancedAnalysis'"
          class="rounded-3xl p-6 dark:bg-[#0d1526]"
        >
          <ExecutionHistory
            :execution-phase-type-filter="[AdvancedAnalysisPhaseType]"
            @reload-advanced-analysis-clicked="onAdvancedAnalysisClicked"
            :enable-pagination="true"
          />
        </Card>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Container that participates in parent flex layout without forcing parent height.
     Only apply flex-fill when FitToBrowser is active via .input-data-step-fill */
  .input-data-step-container {
    display: block;
    flex-direction: column;
    min-height: 0;
  }
  .input-data-step-fill {
    flex: 1 1 0;
    min-height: 0;
  }

  /* Default content area (FitToContent): flow with document.
     When wizard is in FitToBrowser, the container gets .fit-to-browser on the wizard root,
     and this element receives flex-1 + overflow-auto via a class binding. */
  .input-data-step-content {
    display: block;
    min-height: 0;
  }
  /* defensive: when placed inside a fit-to-browser ancestor, ensure internal scrolling works */
  .fit-to-browser .input-data-step-content {
    min-height: 0;
  }

  .card-btn {
    padding: 20px;
    align-items: center;
    display: inline-flex;
    justify-content: center;
    gap: 0.25rem;
    height: 75px;
  }

  .input-data-step-fill {
    flex-direction: column;
    padding-bottom: 0;
    padding-top: 0;
    /* Adjusts for any potential double scrollbar issue */
    margin-bottom: -8px;
  }

  /* Dashboard-like button visuals (kept local to this component) */
  .dashboard-option-circle {
    flex: none;
    align-self: center;
    width: 56px;
    height: 56px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    border-radius: 9999px;
    box-sizing: border-box;
  }

  .dashboard-option-circle .dashboard-option-icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .dashboard-option-icon {
    width: 2rem;
    height: 2rem;
  }

  .dashboard-option-circle.selected,
  .dashboard-option-btn.selected {
    color: var(--primary);
  }

  .dashboard-option-circle.selected:hover,
  .dashboard-option-btn.selected:hover {
    cursor: default;
  }

  .dashboard-option-circle:hover,
  .dashboard-option-btn:hover {
    cursor: pointer;
  }

  .dashboard-option-btn {
    height: 75px;
    max-width: none;
    box-sizing: border-box;
    background: var(--card);
    border: 1px solid var(--border);
    color: inherit;
  }

  .dashboard-option-btn.selected {
    background: var(--primary);
    border: 1px solid transparent;
    color: var(--on-primary, #fff);
  }

  .dashboard-option-btn:not(.selected):hover,
  .dashboard-option-circle:not(.selected):hover {
    border-color: var(--primary-background);
    color: var(--primary);
    background-color: var(bg-blue-100);
  }

  .dark .dashboard-option-btn:not(.selected):hover,
  .dark .dashboard-option-circle:not(.selected):hover {
    border-color: var(--primary-background);
    background-color: var(--black);
    color: var(--primary);
  }

  .dashboard-option-btn:not(.selected):hover .dashboard-option-icon,
  .dashboard-option-circle:not(.selected):hover .dashboard-option-icon {
    color: var(--primary);
  }

  .dashboard-option-btn,
  .dashboard-option-circle {
    transition:
      background-color 0.15s ease,
      color 0.15s ease,
      border-color 0.15s ease;
  }
</style>
